import { useCallback, useEffect, useState } from 'react';
import { AUTH_KEYS } from '../constants/app-constants';
import { convertCurrency, getCurrency, intlNumberFormat, roundDownToUnit } from '../helpers/currency';
import { LoadingService } from '../helpers/loading';
import { getSystemCurrencyRate } from '../services/currencyService';

let cachedRates = null;
let fetchingPromise = null;
let cachedDefaultCurrency = null;

export default function useCurrency() {
  const [ratesData, setRatesData] = useState(cachedRates);
  const [defaultCurrency, setDefaultCurrency] = useState(cachedDefaultCurrency);
  const [ready, setReady] = useState(!!cachedRates);
  const [target, setTarget] = useState(() => getCurrency());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === AUTH_KEYS.LOCAL_STORAGE_USER) {
        const cur = getCurrency();
        if (cur && cur !== target) {
          setTarget(cur);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [target]);

  useEffect(() => {
    const requestId = LoadingService.generateId();

    if (cachedRates) return;
    LoadingService.increment("HOOK CURRENCY", requestId);

    if (!fetchingPromise) {
      fetchingPromise = getSystemCurrencyRate();
    }
    fetchingPromise
      .then((r) => {
        const { defaultCurrency: defCur, ...allRates } = r;
        cachedDefaultCurrency = defCur;
        cachedRates = allRates;

        setDefaultCurrency(defCur);
        setRatesData(allRates);
        setReady(true);
      })
      .catch((err) => {
        //console.error("useCurrency: failed to fetch rates", err);
        setReady(true);
      })
      .finally(() => LoadingService.decrement("HOOK CURRENCY", requestId));
  }, [target]);

  const display = useCallback(
    (amount, from) => {
      const fromCode = from || target;
      if (!fromCode || !target) return "";

      const formatter = intlNumberFormat(target);
      if (fromCode.toLowerCase() === target.toLowerCase()) {
        return formatter.format(roundDownToUnit(amount, target));
      }

      const canConvert =
        !!ratesData &&
        !!ratesData[fromCode.toLowerCase()] &&
        !!ratesData[target.toLowerCase()];

      if (!canConvert) {
        if (!ready) return "";
        const sourceFormatter = intlNumberFormat(fromCode);
        return sourceFormatter.format(roundDownToUnit(amount, fromCode));
      }

      const converted = convertCurrency(amount, fromCode, target, ratesData);
      return formatter.format(roundDownToUnit(converted, target));
    },
    [ratesData, target, ready]
  );

  return { display, ready, target, ratesData, defaultCurrency };
}
